/**
 * @file OutsideFrameField.tsx
 * @description «Фон за рамку сайта — до краёв окна» под шириной блока
 * (phase19.16, партия SB120, этап 2; site-builder.md SB120; проект
 * docs/phases/phase19.16-materials/uiux-sb120-stage2.md §3; данные
 * db-design-sb120-stage2 §1 `look.outsideFrame: true`, нет ключа — в рамке).
 *
 * ```
 * [✓] Фон за рамку сайта — до краёв окна
 *     Рамка сайта 1600 px. Фон блока выйдет за неё до краёв окна, …
 *     На этой ширине рамки нет — разница видна от 1600 px. [Посмотреть на 1920]
 * ```
 *
 * Состояния (§3): рамка «Во всю ширину» — серое со ссылкой «Оформление →
 * Фон и рамка» (SB121); фон блока «Прозрачный» или подложка — серое (фон
 * подложки рисуется в карточке, витрина атрибут не ставит); канва уже рамки —
 * подпись и «Посмотреть на 1920»; «Слить с блоком выше», а верхний в рамке
 * — «!». Включённое при серой причине можно снять (иначе не убрать).
 */

import type { SectionLook } from "@chaiberi/shared";
import { Link } from "react-router";

import { Button } from "../../../ui/button";
import { NoticeStrip } from "../../../ui/NoticeStrip";
import { FieldHint } from "../../../ui/settings-groups";
import { CheckRow } from "../../../site-view/CheckRow";
import { useSectionForm } from "../section-form-context";
import { useBuilderLink } from "../../../../hooks/site-builder/useBuilderLink";
import { useOutsideFrameState } from "../../../../hooks/site-builder/useOutsideFrameState";

/** Ширина канвы, на которой видна рамка. */
const WIDE_CANVAS = 1920;

interface Props {
  look: SectionLook;
  onChange: (look: SectionLook) => void;
}

/** Ссылка на «Оформление → Фон и рамка → Раскладка сайта» (SB121). */
function FrameSettingsLink() {
  const link = useBuilderLink();
  return (
    <Link to={link({ panel: "appearance", sub: "backdrop", group: "frame" })} className="inline-flex min-h-12 items-center font-medium text-primary hover:underline">
      Оформление → Фон и рамка →
    </Link>
  );
}

export function OutsideFrameField({ look, onChange }: Props) {
  const { theme, preview } = useSectionForm();
  const { state, canvasWidth, view, builder } = useOutsideFrameState(look, theme);
  const on = look.outsideFrame === true;

  const set = (value: boolean) => {
    const next = { ...look };
    // Нет ключа — в рамке (как было).
    if (value) next.outsideFrame = true;
    else delete next.outsideFrame;
    preview("look.outsideFrame", value ? true : undefined);
    onChange(next);
  };
  const showWide = () => {
    if (builder) builder.deviceChosen.current = true;
    view?.preview.setWidth(WIDE_CANVAS);
  };

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <CheckRow checked={on} disabled={state.blocked !== null && !on} onChange={set}>
        Фон за рамку сайта — до краёв окна
      </CheckRow>
      <div className="flex min-w-0 flex-col gap-1 pl-8">
        {state.blocked === "frame-full" ? (
          <FieldHint className="mt-0">
            Сайт и так во всю ширину — рамки нет. Рамка: <FrameSettingsLink />
          </FieldHint>
        ) : state.blocked === "transparent" ? (
          <FieldHint className="mt-0">У блока нет своего фона — за рамкой нечего показывать. Выберите цвет или фото в «Фоне».</FieldHint>
        ) : state.blocked === "card" ? (
          <FieldHint className="mt-0">У блока подложка — фон за рамку не выходит. Снимите подложку в «Ширине и высоте».</FieldHint>
        ) : (
          <>
            <FieldHint className="mt-0">
              Рамка сайта {state.frameWidth} px. Фон блока выйдет за неё до краёв окна, содержимое останется шириной блока. Видно на
              экранах шире {state.frameWidth}. Шапка и подвал остаются в рамке.
            </FieldHint>
            {state.narrowCanvas ? <FieldHint className="mt-0">На этой ширине рамки нет — разница видна от {state.frameWidth} px.</FieldHint> : null}
            {view && canvasWidth !== WIDE_CANVAS ? (
              <Button variant="outline" className="self-start" onClick={showWide}>
                Посмотреть на 1920
              </Button>
            ) : null}
          </>
        )}
      </div>
      {state.mergeWarning ? (
        <NoticeStrip tone="warning" className="mt-1">
          Блок выше в рамке — общий фон склеится только внутри рамки.
        </NoticeStrip>
      ) : null}
    </div>
  );
}
